import dayjs from 'dayjs'
import { getEnvironmentValue } from 'utils/helpers'
import { DATE_FORMAT } from './constants'
import { CheckIsDynamicTariffEligible, CheckSettlementFrequencyEligible, StartDateRange } from './types'
import { Address } from 'types/customer'
import { PARTNER_IDS } from 'constants/constants'
import {
  COUNTER_TYPES,
  PRODUCT_TYPES,
  PRODUCTS_CONFIG,
  Region,
  REGIONS,
  SIMULATION_TYPES,
  SimulationSalesOffice,
  SimulationType,
  Situation,
  SITUATIONS
} from '@boltenergy-be/shared'
import { MarketingDataKeys, MarketingStore } from 'store/marketing/types'

/**
 * Formats a given address to a string
 *
 * @param {Address} address
 * @param {[boolean]} singleLine
 * @returns
 */
export const addressToString = (address: Address, singleLine?: boolean): string => {
  return singleLine
    ? `${address.streetName} ${address.streetNumber}${address.streetBox ? ` ${address.streetBox}` : ''}, ${address.postalCode} ${
        address.townName
      }`
    : `${address.streetName} ${address.streetNumber} ${address.streetBox}<br />${address.postalCode} ${address.townName}`
}

/**
 * Formats a given date to a string in the dd/MM/yyyy format
 *
 * @param date
 * @returns
 */
export const dateToString = (date: string): string => {
  return dayjs(date).format(DATE_FORMAT)
}

/**
 * Returns the date range for the start date based on the given situation & isCompany parameters
 *
 * @param {Situation} situation
 * @returns {StartDateRange}
 */
export const getStartDateRange = (situation: Situation): StartDateRange => {
  const now = dayjs()

  switch (situation) {
    case SITUATIONS.newConnection: {
      return {
        initial: now.toISOString(),
        max: now.toISOString(),
        min: now.toISOString()
      }
    }

    case SITUATIONS.switch: {
      return {
        initial: now.add(25, 'days').toISOString(),
        max: now.add(12, 'months').toISOString(),
        min: now.add(25, 'days').toISOString()
      }
    }

    case SITUATIONS.move: {
      return {
        initial: now.toISOString(),
        max: now.add(10, 'months').toISOString(),
        min: now.subtract(25, 'days').toISOString()
      }
    }

    default:
      return {
        initial: now.toISOString(),
        max: now.toISOString(),
        min: now.toISOString()
      }
  }
}

/**
 * Returns the contract start date based on the given min date, opportunity start date, partner ID and sales office
 *
 * @param {string} min
 * @param {string} opportunityStartDate
 * @param {string | undefined} partnerId
 * @param {SimulationSalesOffice | string} simSalesOffice
 * @returns {string}
 */
export const getContractMinStartDate = (
  min: string,
  opportunityStartDate: string,
  partnerId: string,
  simSalesOffice: SimulationSalesOffice | string
): string => {
  // Only allow extended min start date when the contract is created by a superuser and the type is 'Extra Address' and the opportunity start date is before the min date
  if (
    partnerId === getEnvironmentValue(PARTNER_IDS.BOLT_ENERGIE_SUPERUSER) &&
    ['extra_address', 'move'].includes(simSalesOffice) &&
    dayjs(opportunityStartDate) < dayjs(min)
  ) {
    return dayjs(opportunityStartDate).subtract(1, 'day').toISOString()
  }

  return min
}

/**
 * Checks if the given simulation type should show the price simulation section & relevant modals
 * @param {SimulationType} simulationType
 * @returns {boolean}
 */
export const checkHasPriceSimulationSection = (simulationType: SimulationType): boolean => {
  return [SIMULATION_TYPES.express, SIMULATION_TYPES.pricingSimulation].some((type) => type === simulationType)
}

/**
 * Checks if the given simulation type should show edit usage drawer trigger & modal
 * @param {SimulationType} simulationType
 * @returns {boolean}
 */
export const checkHasEditUsageDrawer = (simulationType: SimulationType): boolean => {
  return [SIMULATION_TYPES.express, SIMULATION_TYPES.pricingSimulation].some((type) => type === simulationType)
}

/**
 * Check if the customer should identify with itsme
 * @param {Region} region
 * @param {boolean} isCompany
 * @param {MarketingStore} marketing
 * @returns {boolean}
 */
export const checkShouldIdentifyWithItsme = (
  region: Region,
  isCompany: boolean,
  marketing: Pick<MarketingStore, MarketingDataKeys.SKIP_ITSME | MarketingDataKeys.CONFIRMATION_FROM_PORTAL_REGISTRATION>
): boolean => {
  return (
    region !== REGIONS.flanders &&
    !isCompany &&
    !marketing[MarketingDataKeys.SKIP_ITSME] &&
    !marketing[MarketingDataKeys.CONFIRMATION_FROM_PORTAL_REGISTRATION]
  )
}

/**
 * Check if the dynamic
 * @param {CheckIsDynamicTariffEligible} params
 * @returns {boolean}
 */
export const checkDynamicTariffEligible = ({
  proposition,
  counterType,
  region,
  hasExclusiveNightMeter
}: CheckIsDynamicTariffEligible): boolean => {
  return (
    proposition in PRODUCTS_CONFIG &&
    PRODUCTS_CONFIG[proposition].type !== PRODUCT_TYPES.fix &&
    counterType === COUNTER_TYPES.digital &&
    region === REGIONS.flanders &&
    !hasExclusiveNightMeter
  )
}

/**
 *
 * @param {CheckSettlementFrequencyEligible} params
 * @returns {boolean}
 */
export const checkSetllementFrequencyEligible = ({ eventID, region, counterType }: CheckSettlementFrequencyEligible): boolean => {
  return !!eventID && counterType === COUNTER_TYPES.digital && region === REGIONS.flanders
}
